import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckSquare, Loader2, Save, ShieldCheck, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/efs/PageHeader';
import { cn } from '@/lib/utils';

import { useCurrentUser } from '@/lib/auth-context';

import { usePermissionsList } from '@/features/settings/roles/useRoles';
import type { PermissionDto } from '@/features/settings/roles/types';

import { canAuthorWorkPackages, resolveI18nText } from './helpers';
import { useUpdateWorkPackage, useWorkPackagesList } from './useWorkPackages';
import type { I18nText } from './types';

const NS = 'settings';

interface PermissionGroup {
  category: string;
  rows: PermissionDto[];
}

const sortedKey = (codes: string[]) => [...codes].sort().join('|');

interface NameDraft {
  ja: string;
  zh: string;
  en: string;
}

const toNameDraft = (blob: I18nText | null | undefined): NameDraft => ({
  ja: blob?.ja ?? '',
  zh: blob?.['zh-TW'] ?? '',
  en: blob?.en ?? '',
});

const fromNameDraft = (draft: NameDraft): I18nText => {
  const blob: I18nText = {};
  if (draft.ja.trim()) blob.ja = draft.ja.trim();
  if (draft.zh.trim()) blob['zh-TW'] = draft.zh.trim();
  if (draft.en.trim()) blob.en = draft.en.trim();
  return blob;
};

const sameI18n = (a: I18nText, b: I18nText | null | undefined): boolean =>
  JSON.stringify(a) === JSON.stringify(b ?? {});

export const WorkPackageEditPage = () => {
  const { t, i18n } = useTranslation(NS);
  const { workPackageId = '' } = useParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const canAuthor = canAuthorWorkPackages(user);
  const query = useWorkPackagesList();
  const permissionsQuery = usePermissionsList();
  const update = useUpdateWorkPackage();
  const pkg = (query.data ?? []).find((row) => row.work_package_id === workPackageId);

  const [draftPermissions, setDraftPermissions] = useState<string[]>([]);
  const [nameDraft, setNameDraft] = useState<NameDraft>({ ja: '', zh: '', en: '' });
  const [descriptionDraft, setDescriptionDraft] = useState<NameDraft>({ ja: '', zh: '', en: '' });
  const [affects, setAffects] = useState({ quotation: false, report: false, settlement: false });
  const [activeFlag, setActiveFlag] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('');

  const groups = useMemo<PermissionGroup[]>(() => {
    const map = new Map<string, PermissionDto[]>();
    (permissionsQuery.data ?? [])
      .filter((permission) => permission.active_flag && permission.grantable_flag)
      .forEach((permission) => {
        const rows = map.get(permission.category) ?? [];
        rows.push(permission);
        map.set(permission.category, rows);
      });
    return Array.from(map.entries()).map(([category, rows]) => ({
      category,
      rows: rows.sort((a, b) => a.sort_order - b.sort_order),
    }));
  }, [permissionsQuery.data]);

  useEffect(() => {
    if (!pkg) return;
    setDraftPermissions(pkg.permission_codes);
    setNameDraft(toNameDraft(pkg.name_i18n));
    setDescriptionDraft(toNameDraft(pkg.description_i18n));
    setAffects({
      quotation: pkg.affects_quotation,
      report: pkg.affects_report,
      settlement: pkg.affects_settlement,
    });
    setActiveFlag(pkg.active_flag);
  }, [pkg]);

  useEffect(() => {
    if (!activeCategory && groups[0]) setActiveCategory(groups[0].category);
  }, [activeCategory, groups]);

  const activeGroup = groups.find((group) => group.category === activeCategory) ?? groups[0];

  const permissionLabel = (permission: PermissionDto) =>
    t(`efs.settings.roles.permissions.${permission.permission_code}`, {
      defaultValue: permission.display_name || permission.permission_code,
    });
  const permissionDescription = (permission: PermissionDto) =>
    t(`efs.settings.roles.permission_descriptions.${permission.permission_code}`, {
      defaultValue: permission.description || '',
    });
  const categoryLabel = (category: string) =>
    t(`efs.settings.roles.categories.${category}`, { defaultValue: category });
  const categoryDescription = (category: string) =>
    t(`efs.settings.roles.category_descriptions.${category}`, {
      defaultValue: t('efs.settings.roles.category_default_description'),
    });

  const togglePermission = (permissionCode: string, checked: boolean) => {
    setDraftPermissions((current) => {
      if (checked) return Array.from(new Set([...current, permissionCode])).sort();
      return current.filter((permission) => permission !== permissionCode);
    });
  };

  const setCategoryPermissions = (checked: boolean) => {
    if (!activeGroup) return;
    const codes = activeGroup.rows.map((permission) => permission.permission_code);
    setDraftPermissions((current) => {
      if (checked) return Array.from(new Set([...current, ...codes])).sort();
      return current.filter((permission) => !codes.includes(permission));
    });
  };

  const nameBlob = fromNameDraft(nameDraft);
  const descriptionBlob = fromNameDraft(descriptionDraft);
  const dirty = pkg
    ? sortedKey(draftPermissions) !== sortedKey(pkg.permission_codes) ||
      !sameI18n(nameBlob, pkg.name_i18n) ||
      !sameI18n(descriptionBlob, pkg.description_i18n) ||
      affects.quotation !== pkg.affects_quotation ||
      affects.report !== pkg.affects_report ||
      affects.settlement !== pkg.affects_settlement ||
      activeFlag !== pkg.active_flag
    : false;
  const nameValid = !!nameBlob.ja;

  const save = async () => {
    if (!pkg) return;
    await update.mutateAsync({
      id: pkg.work_package_id,
      body: {
        name_i18n: nameBlob,
        description_i18n: descriptionBlob,
        affects_quotation: affects.quotation,
        affects_report: affects.report,
        affects_settlement: affects.settlement,
        active_flag: activeFlag,
        permission_codes: draftPermissions,
      },
    });
    navigate('/settings/work-packages');
  };

  const selectedRows = (activeGroup?.rows ?? []).filter((permission) =>
    draftPermissions.includes(permission.permission_code),
  );
  const categorySelectedCount = (group: PermissionGroup) =>
    group.rows.filter((permission) => draftPermissions.includes(permission.permission_code)).length;

  if (query.isLoading || permissionsQuery.isLoading) {
    return (
      <div className="py-12 text-center text-sm text-efs-text-muted">
        {t('efs.common.messages.loading', { ns: 'common' })}
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={t('efs.settings.work_packages.not_found_title')}
          subtitle={t('efs.settings.work_packages.not_found_subtitle', { workPackageId })}
        />
        <Button asChild variant="outline">
          <Link to="/settings/work-packages">
            <ArrowLeft className="h-4 w-4" />
            {t('efs.settings.work_packages.actions.back_to_list')}
          </Link>
        </Button>
      </div>
    );
  }

  const headingName = resolveI18nText(pkg.name_i18n, i18n.language) || pkg.package_code;
  const affectsFields = [
    { key: 'quotation' as const, label: t('efs.settings.work_packages.affects.quotation') },
    { key: 'report' as const, label: t('efs.settings.work_packages.affects.report') },
    { key: 'settlement' as const, label: t('efs.settings.work_packages.affects.settlement') },
  ];
  const nameFields = [
    { key: 'ja' as const, label: t('efs.settings.work_packages.fields.name_ja') },
    { key: 'zh' as const, label: t('efs.settings.work_packages.fields.name_zh') },
    { key: 'en' as const, label: t('efs.settings.work_packages.fields.name_en') },
  ];
  const descriptionFields = [
    { key: 'ja' as const, label: t('efs.settings.work_packages.fields.description_ja') },
    { key: 'zh' as const, label: t('efs.settings.work_packages.fields.description_zh') },
    { key: 'en' as const, label: t('efs.settings.work_packages.fields.description_en') },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settings.work_packages.edit_title', { name: headingName })}
        subtitle={t('efs.settings.work_packages.edit_subtitle')}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/settings/work-packages">
                <ArrowLeft className="h-4 w-4" />
                {t('efs.settings.work_packages.actions.back_to_list')}
              </Link>
            </Button>
            {canAuthor ? (
              <Button
                onClick={() => void save()}
                disabled={!dirty || !nameValid || update.isPending}
              >
                {update.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t('efs.settings.work_packages.actions.save')}
              </Button>
            ) : null}
          </>
        }
      />

      <section className="rounded-md border border-efs-border bg-efs-bg-panel px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-efs-text-primary">{headingName}</h2>
              {activeFlag ? null : (
                <Badge variant="outline">{t('efs.settings.work_packages.inactive')}</Badge>
              )}
            </div>
            <p className="mt-1 break-all text-xs text-efs-text-muted">{pkg.package_code}</p>
          </div>
          {!canAuthor ? (
            <p className="mt-3 w-full rounded-md bg-efs-bg-subtle px-3 py-2 text-xs text-efs-text-muted">
              {t('efs.settings.work_packages.read_only_notice')}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 text-xs text-efs-text-muted">
            <span className="rounded-md border border-efs-border px-2 py-1">
              {t('efs.settings.work_packages.summary.permissions', {
                count: draftPermissions.length,
              })}
            </span>
            {pkg.party_type ? (
              <span className="rounded-md border border-efs-border px-2 py-1">
                {t(`efs.settings.companies.types.${pkg.party_type}`, {
                  defaultValue: pkg.party_type,
                })}
              </span>
            ) : null}
          </div>
        </div>
      </section>

      <section className="grid gap-4 rounded-md border border-efs-border bg-efs-bg-panel px-4 py-4 md:grid-cols-2">
        <div className="space-y-3 md:col-span-2">
          <h3 className="text-sm font-semibold text-efs-text-primary">
            {t('efs.settings.work_packages.detail_section')}
          </h3>
        </div>
        {nameFields.map((entry) => (
          <label key={`name-${entry.key}`} className="block space-y-1">
            <span className="text-xs font-medium text-efs-text-muted">{entry.label}</span>
            <Input
              disabled={!canAuthor}
              value={nameDraft[entry.key]}
              onChange={(event) =>
                setNameDraft((current) => ({ ...current, [entry.key]: event.target.value }))
              }
            />
          </label>
        ))}
        {descriptionFields.map((entry) => (
          <label key={`desc-${entry.key}`} className="block space-y-1">
            <span className="text-xs font-medium text-efs-text-muted">{entry.label}</span>
            <Textarea
              rows={2}
              disabled={!canAuthor}
              value={descriptionDraft[entry.key]}
              onChange={(event) =>
                setDescriptionDraft((current) => ({ ...current, [entry.key]: event.target.value }))
              }
            />
          </label>
        ))}
        <div className="space-y-2 md:col-span-2">
          <span className="text-xs font-medium text-efs-text-muted">
            {t('efs.settings.work_packages.fields.affects')}
          </span>
          <div className="flex flex-wrap gap-4">
            {affectsFields.map((entry) => (
              <label key={entry.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  disabled={!canAuthor}
                  checked={affects[entry.key]}
                  onChange={(event) =>
                    setAffects((current) => ({ ...current, [entry.key]: event.target.checked }))
                  }
                />
                {entry.label}
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm md:col-span-2">
          <input
            type="checkbox"
            className="h-4 w-4"
            disabled={!canAuthor}
            checked={activeFlag}
            onChange={(event) => setActiveFlag(event.target.checked)}
          />
          {t('efs.settings.work_packages.fields.active')}
        </label>
      </section>

      <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <nav className="rounded-md border border-efs-border bg-efs-bg-panel p-2">
          <div className="px-2 pb-2 text-xs font-semibold text-efs-text-muted">
            {t('efs.settings.roles.feature_menu')}
          </div>
          <div className="space-y-1">
            {groups.map((group) => {
              const selected = categorySelectedCount(group);
              return (
                <button
                  key={group.category}
                  type="button"
                  onClick={() => setActiveCategory(group.category)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-efs-bg-subtle',
                    activeGroup?.category === group.category && 'bg-efs-bg-subtle font-medium',
                  )}
                >
                  <span className="truncate">{categoryLabel(group.category)}</span>
                  <span className="shrink-0 text-xs text-efs-text-muted">
                    {selected}/{group.rows.length}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <section className="min-w-0 space-y-3">
          <div className="rounded-md border border-efs-border bg-efs-bg-panel px-4 py-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-efs-text-primary">
                  {activeGroup ? categoryLabel(activeGroup.category) : '—'}
                </h3>
                <p className="mt-1 text-sm text-efs-text-muted">
                  {activeGroup ? categoryDescription(activeGroup.category) : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!canAuthor || !activeGroup}
                  onClick={() => setCategoryPermissions(true)}
                >
                  <CheckSquare className="h-4 w-4" />
                  {t('efs.settings.roles.actions.select_feature_all')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!canAuthor || !activeGroup}
                  onClick={() => setCategoryPermissions(false)}
                >
                  <XCircle className="h-4 w-4" />
                  {t('efs.settings.roles.actions.clear_feature_all')}
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-efs-border bg-efs-bg-panel">
            <div className="grid grid-cols-[4rem_minmax(12rem,1fr)_9rem] border-b border-efs-border bg-efs-bg-subtle px-3 py-2 text-xs font-semibold text-efs-text-muted">
              <span>{t('efs.settings.roles.fields.enabled')}</span>
              <span>{t('efs.settings.roles.fields.permission')}</span>
              <span>{t('efs.settings.roles.fields.action')}</span>
            </div>
            <div className="divide-y divide-efs-border">
              {(activeGroup?.rows ?? []).map((permission) => {
                const checked = draftPermissions.includes(permission.permission_code);
                return (
                  <label
                    key={permission.permission_code}
                    className="grid cursor-pointer grid-cols-[4rem_minmax(12rem,1fr)_9rem] gap-2 px-3 py-3 hover:bg-efs-bg-subtle"
                  >
                    <span>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        disabled={!canAuthor}
                        checked={checked}
                        onChange={(event) =>
                          togglePermission(permission.permission_code, event.target.checked)
                        }
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-efs-text-primary">
                        {permissionLabel(permission)}
                      </span>
                      <span className="mt-1 block text-xs text-efs-text-muted">
                        {permissionDescription(permission) || permission.permission_code}
                      </span>
                    </span>
                    <span className="text-xs text-efs-text-muted">
                      <span className="rounded-md border border-efs-border px-2 py-1">
                        {permission.action}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <section className="rounded-md border border-efs-border bg-efs-bg-panel px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-efs-text-primary">
              <ShieldCheck className="h-4 w-4" />
              {t('efs.settings.roles.allowed_actions_title')}
            </div>
            {selectedRows.length > 0 ? (
              <ul className="mt-3 grid gap-2 md:grid-cols-2">
                {selectedRows.map((permission) => (
                  <li
                    key={permission.permission_code}
                    className="rounded-md border border-efs-border px-3 py-2"
                  >
                    <div className="text-sm font-medium text-efs-text-primary">
                      {permissionLabel(permission)}
                    </div>
                    <div className="mt-1 text-xs text-efs-text-muted">
                      {permissionDescription(permission) || permission.permission_code}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-efs-text-muted">
                {t('efs.settings.roles.no_allowed_actions')}
              </p>
            )}
          </section>
        </section>
      </div>
    </div>
  );
};

export default WorkPackageEditPage;
